import React from 'react';
import { Document, Page, View, Text, StyleSheet, Svg, Rect, Line } from '@react-pdf/renderer';
import { AnalyticsSummary, QRCodeData } from '../types';

const COLORS = {
  navy: '#101F1C',
  lime: '#D4FF80',
  slateDark: '#2D3E3A',
  slateMedium: '#5C6E6A',
  slateFaint: '#F1F4F3',
  white: '#FFFFFF',
  gridline: '#E5E7EB',
};

const BAR_COLORS = [COLORS.navy, '#5C6E6A', '#8FA39D', '#B7C9C3', '#D4E4DF'];

// A4 portrait, 36pt margins each side -> ~523pt of usable width. Every chart
// is sized off this so the report reliably fits a single page instead of
// spilling to a second one.
const CONTENT_WIDTH = 523;

const styles = StyleSheet.create({
  page: {
    fontFamily: 'Helvetica',
    fontSize: 9,
    color: COLORS.navy,
    paddingBottom: 34,
  },
  header: {
    backgroundColor: COLORS.navy,
    paddingHorizontal: 36,
    paddingTop: 18,
    paddingBottom: 12,
  },
  headerTitle: {
    fontFamily: 'Times-Bold',
    fontSize: 17,
    color: COLORS.lime,
  },
  headerSubtitle: {
    fontSize: 8,
    color: COLORS.white,
    marginTop: 4,
  },
  body: {
    paddingHorizontal: 36,
    paddingTop: 14,
  },
  qrTitle: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 13,
    marginBottom: 3,
  },
  qrDescription: {
    fontSize: 8.5,
    color: COLORS.slateDark,
    marginBottom: 3,
  },
  qrUrl: {
    fontSize: 8,
    color: COLORS.slateMedium,
    marginBottom: 10,
  },
  statsRow: {
    flexDirection: 'row',
    backgroundColor: COLORS.slateFaint,
    borderRadius: 4,
    paddingVertical: 9,
    marginBottom: 12,
  },
  statCell: {
    flex: 1,
    alignItems: 'center',
  },
  statValue: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 14,
    marginBottom: 3,
  },
  statLabel: {
    fontSize: 7.5,
    color: COLORS.slateDark,
  },
  sectionTitle: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 10,
    marginBottom: 6,
    marginTop: 10,
  },
  emptyText: {
    fontSize: 8,
    color: COLORS.slateMedium,
    fontStyle: 'italic',
  },
  footer: {
    position: 'absolute',
    bottom: 14,
    left: 36,
    right: 36,
    flexDirection: 'row',
    justifyContent: 'space-between',
    borderTopWidth: 0.5,
    borderTopColor: COLORS.gridline,
    paddingTop: 5,
    fontSize: 7,
    color: '#9CA3AF',
  },
  chartCard: {
    backgroundColor: COLORS.white,
    border: `0.5pt solid ${COLORS.gridline}`,
    borderRadius: 4,
    padding: 8,
  },
  chartCardTitle: {
    fontSize: 8,
    fontFamily: 'Helvetica-Bold',
    marginBottom: 5,
  },
  chartsRow: {
    flexDirection: 'row',
    gap: 10,
  },
  chartColumn: {
    flex: 1,
  },
  hbarRow: {
    marginBottom: 4,
  },
  hbarLabelRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 1,
  },
  hbarLabel: {
    fontSize: 7,
    color: COLORS.slateDark,
  },
  hbarValue: {
    fontSize: 7,
    fontFamily: 'Helvetica-Bold',
    color: COLORS.navy,
  },
  hbarTrack: {
    height: 4,
    borderRadius: 2,
    backgroundColor: COLORS.slateFaint,
  },
  hbarFill: {
    height: 4,
    borderRadius: 2,
  },
});

function pct(count: number, total: number): string {
  return total > 0 ? `${((count / total) * 100).toFixed(1)}%` : '0%';
}

// --- Axis-labeled bar chart (Scans Over Time / Peak Scan Hours) ----------

const AXIS_COL_WIDTH = 24;
const CHART_HEIGHT = 56;
const CHART_BAR_GAP = 2;

interface TimeSeriesPoint {
  label: string;
  value: number;
}

function TimeSeriesBarChart({
  data,
  width,
  labelEvery,
  emptyMessage,
}: {
  data: TimeSeriesPoint[];
  width: number;
  labelEvery: number;
  emptyMessage: string;
}) {
  if (!data || data.length === 0) {
    return <Text style={styles.emptyText}>{emptyMessage}</Text>;
  }

  const chartWidth = width - AXIS_COL_WIDTH;
  const max = Math.max(...data.map((d) => d.value), 1);
  const barSlot = chartWidth / data.length;
  const barWidth = Math.max(0.75, barSlot - CHART_BAR_GAP);

  return (
    <View style={{ position: 'relative', height: CHART_HEIGHT + 14 }}>
      {/* Y-axis value labels */}
      <Text style={{ position: 'absolute', left: 0, top: -3, width: AXIS_COL_WIDTH - 4, fontSize: 5.5, color: COLORS.slateMedium, textAlign: 'right' }}>
        {max.toLocaleString()}
      </Text>
      <Text style={{ position: 'absolute', left: 0, top: CHART_HEIGHT - 5, width: AXIS_COL_WIDTH - 4, fontSize: 5.5, color: COLORS.slateMedium, textAlign: 'right' }}>
        0
      </Text>

      <Svg width={width} height={CHART_HEIGHT} viewBox={`0 0 ${width} ${CHART_HEIGHT}`}>
        <Line x1={AXIS_COL_WIDTH} y1={0} x2={width} y2={0} stroke={COLORS.gridline} strokeWidth={0.5} />
        <Line x1={AXIS_COL_WIDTH} y1={CHART_HEIGHT / 2} x2={width} y2={CHART_HEIGHT / 2} stroke={COLORS.gridline} strokeWidth={0.5} />
        <Line x1={AXIS_COL_WIDTH} y1={CHART_HEIGHT} x2={width} y2={CHART_HEIGHT} stroke={COLORS.gridline} strokeWidth={0.5} />
        {data.map((d, i) => {
          const barHeight = (d.value / max) * (CHART_HEIGHT - 3);
          const x = AXIS_COL_WIDTH + i * barSlot;
          const y = CHART_HEIGHT - barHeight;
          return <Rect key={`${d.label}-${i}`} x={x} y={y} width={barWidth} height={barHeight} fill={COLORS.navy} rx={0.75} />;
        })}
      </Svg>

      {/* X-axis labels, thinned out so they don't collide */}
      {data.map((d, i) =>
        i % labelEvery === 0 ? (
          <Text
            key={`label-${d.label}-${i}`}
            style={{
              position: 'absolute',
              top: CHART_HEIGHT + 2,
              left: AXIS_COL_WIDTH + i * barSlot - barSlot / 2,
              width: barSlot * labelEvery,
              fontSize: 5.5,
              color: COLORS.slateMedium,
              textAlign: 'center',
            }}
          >
            {d.label}
          </Text>
        ) : null
      )}
    </View>
  );
}

// --- Labeled horizontal bar list (Devices / Browsers / Countries / URLs) -

function HorizontalBarList({
  title,
  items,
  total,
}: {
  title: string;
  items: { label: string; count: number }[] | undefined;
  total: number;
}) {
  const max = Math.max(...(items || []).map((i) => i.count), 1);

  return (
    <View style={styles.chartCard} wrap={false}>
      <Text style={styles.chartCardTitle}>{title}</Text>
      {items && items.length > 0 ? (
        items.map((item, i) => (
          <View key={item.label} style={styles.hbarRow}>
            <View style={styles.hbarLabelRow}>
              <Text style={styles.hbarLabel}>{item.label}</Text>
              <Text style={styles.hbarValue}>
                {item.count.toLocaleString()} ({pct(item.count, total)})
              </Text>
            </View>
            <View style={styles.hbarTrack}>
              <View
                style={{
                  ...styles.hbarFill,
                  width: `${(item.count / max) * 100}%`,
                  backgroundColor: BAR_COLORS[i % BAR_COLORS.length],
                }}
              />
            </View>
          </View>
        ))
      ) : (
        <Text style={styles.emptyText}>No data</Text>
      )}
    </View>
  );
}

interface QrReportDocumentProps {
  qrCode: QRCodeData;
  analytics: AnalyticsSummary;
}

const HOUR_LABELS = ['12a', '', '', '3a', '', '', '6a', '', '', '9a', '', '', '12p', '', '', '3p', '', '', '6p', '', '', '9p', '', ''];

export function QrReportDocument({ qrCode, analytics }: QrReportDocumentProps) {
  const uniqueRate = pct(analytics.unique_users, analytics.total_scans);

  const stats = [
    { label: 'Total Scans', value: analytics.total_scans.toLocaleString() },
    { label: 'Unique Users', value: analytics.unique_users.toLocaleString() },
    { label: 'Unique Rate', value: uniqueRate },
    {
      label: 'Last Scan',
      value: analytics.last_scan_at ? new Date(analytics.last_scan_at).toLocaleDateString() : 'Never',
    },
  ];

  const dailySeries: TimeSeriesPoint[] = (analytics.daily_scans || []).map((d) => ({
    label: new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    value: d.count,
  }));

  const hourlySeries: TimeSeriesPoint[] = Array.from({ length: 24 }, (_, hour) => {
    const point = (analytics.hourly_breakdown || []).find((h) => h.hour === hour);
    return { label: HOUR_LABELS[hour], value: point?.count || 0 };
  });

  const halfWidth = (CONTENT_WIDTH - 10) / 2 - 16; // minus chartsRow gap and chartCard padding

  return (
    <Document title={`${qrCode.title} - QR Code Analytics Report`}>
      <Page size="A4" style={styles.page}>
        <View style={styles.header} fixed>
          <Text style={styles.headerTitle}>QR Code Analytics Report</Text>
          <Text style={styles.headerSubtitle}>
            Generated:{' '}
            {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </Text>
        </View>

        <View style={styles.body}>
          <Text style={styles.qrTitle}>{qrCode.title}</Text>
          {qrCode.description ? <Text style={styles.qrDescription}>{qrCode.description}</Text> : null}
          <Text style={styles.qrUrl}>URL: {qrCode.fallback_url}</Text>

          <View style={styles.statsRow}>
            {stats.map((stat) => (
              <View key={stat.label} style={styles.statCell}>
                <Text style={styles.statValue}>{stat.value}</Text>
                <Text style={styles.statLabel}>{stat.label}</Text>
              </View>
            ))}
          </View>

          <Text style={styles.sectionTitle}>Scans Over Time &amp; Peak Hours</Text>
          <View style={styles.chartsRow}>
            <View style={[styles.chartColumn, styles.chartCard]}>
              <Text style={styles.chartCardTitle}>Daily scans</Text>
              <TimeSeriesBarChart
                data={dailySeries}
                width={halfWidth}
                labelEvery={Math.max(1, Math.ceil(dailySeries.length / 6))}
                emptyMessage="No daily scan data available for this period."
              />
            </View>
            <View style={[styles.chartColumn, styles.chartCard]}>
              <Text style={styles.chartCardTitle}>Scans by hour of day</Text>
              <TimeSeriesBarChart data={hourlySeries} width={halfWidth} labelEvery={3} emptyMessage="No hourly scan data available." />
            </View>
          </View>

          <Text style={styles.sectionTitle}>Breakdown</Text>
          <View style={styles.chartsRow} wrap={false}>
            <View style={styles.chartColumn}>
              <HorizontalBarList
                title="Devices"
                items={analytics.device_breakdown?.map((d) => ({ label: d.device, count: d.count }))}
                total={analytics.total_scans}
              />
            </View>
            <View style={styles.chartColumn}>
              <HorizontalBarList
                title="Browsers"
                items={analytics.browser_breakdown?.map((b) => ({ label: b.browser, count: b.count }))}
                total={analytics.total_scans}
              />
            </View>
            <View style={styles.chartColumn}>
              <HorizontalBarList
                title="Top Countries"
                items={analytics.country_breakdown
                  ?.slice(0, 5)
                  .map((c) => ({ label: c.country, count: c.count }))}
                total={analytics.total_scans}
              />
            </View>
          </View>

          {analytics.url_breakdown && analytics.url_breakdown.length > 0 && (
            <>
              <Text style={styles.sectionTitle}>Destination URLs</Text>
              <HorizontalBarList
                title="Where scans were routed"
                items={analytics.url_breakdown.slice(0, 4).map((u) => ({ label: u.url, count: u.count }))}
                total={analytics.total_scans}
              />
            </>
          )}
        </View>

        <View style={styles.footer} fixed>
          <Text>Generated by Clinqr</Text>
          <Text render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`} />
          <Text>clinqr.com</Text>
        </View>
      </Page>
    </Document>
  );
}
